import * as React from "react";
import { useLocation } from "react-router";
import { toast } from "react-toastify";
import { FILES_TAGS, filesApi, useGetFileDetailsQuery } from "@/api/files";
import { Dialog } from "@/components/ui/dialog";
import { useAuthenticatedUser } from "@/hooks/use-authenticated-user";
import { useAppDispatch } from "@/store/hooks";
import { type FolderItem } from "@/types/fse";
import { UploadModalContent } from "@/views/drive/modals/uploadModal/content";
import { collectDroppedFiles, DirectoryTraversalUnsupportedError, InvalidDroppedPathError } from "./directoryCollector";
import { GlobalUploadContext } from "./globalUploadContext";
import { GlobalDropOverlay } from "./GlobalDropOverlay";
import { DirectoryUploadOperationTracker } from "./DirectoryUploadOperationTracker";
import { getCurrentDriveFolderId, resolveUploadDestination } from "./uploadDestination";
import { type UploadDestination } from "./uploadCoordinator";
import { type CollectedUploadFile, type UploadDialogState } from "./types";

const GENERIC_DROP_ERROR = "The dropped files could not be read.";

interface Props {
  children: React.ReactNode;
}

const isExternalFileDrag = (event: DragEvent): boolean => Array.from(event.dataTransfer?.types ?? []).includes("Files");

const asFolder = (value: unknown): FolderItem | undefined => {
  if (
    typeof value !== "object" ||
    value === null ||
    !("type" in value) ||
    value.type !== "folder" ||
    !("id" in value) ||
    typeof value.id !== "string" ||
    value.id.trim().length === 0 ||
    !("name" in value) ||
    typeof value.name !== "string" ||
    value.name.trim().length === 0 ||
    !("permissions" in value) ||
    !Array.isArray(value.permissions) ||
    value.permissions.some(
      permission =>
        typeof permission !== "object" ||
        permission === null ||
        !("id" in permission) ||
        typeof permission.id !== "string" ||
        permission.id.trim().length === 0 ||
        !("fse_id" in permission) ||
        typeof permission.fse_id !== "string" ||
        permission.fse_id.trim().length === 0 ||
        permission.fse_id !== value.id ||
        !("user_id" in permission) ||
        typeof permission.user_id !== "string" ||
        permission.user_id.trim().length === 0 ||
        !("role" in permission) ||
        (permission.role !== "owner" && permission.role !== "viewer"),
    )
  ) {
    return undefined;
  }
  return value as FolderItem;
};

export const GlobalUploadProvider = ({ children }: Props) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const authenticatedUser = useAuthenticatedUser();
  const currentFolderId = getCurrentDriveFolderId(location.pathname);
  const rootQuery = useGetFileDetailsQuery({});
  const currentQuery = useGetFileDetailsQuery({ fse_id: currentFolderId }, { skip: currentFolderId === undefined });
  const boundaryRef = React.useRef<HTMLDivElement>(null);
  const dragDepthRef = React.useRef(0);
  const [isFileDragActive, setIsFileDragActive] = React.useState(false);
  const dialogGenerationRef = React.useRef(0);
  const activeDialogGenerationRef = React.useRef<number | null>(null);
  const dialogBusyRef = React.useRef(false);
  const dialogVisibleRef = React.useRef(false);
  const [activeDialog, setActiveDialog] = React.useState<{
    generation: number;
    state: UploadDialogState;
  } | null>(null);
  const [isUploadDialogVisible, setIsUploadDialogVisible] = React.useState(false);
  const [trackedOperationIds, setTrackedOperationIds] = React.useState<string[]>([]);
  const dialogState = isUploadDialogVisible ? (activeDialog?.state ?? null) : null;

  const rootFolder = rootQuery.isError ? undefined : asFolder(rootQuery.data);
  const currentFolder = currentQuery.isError ? undefined : asFolder(currentQuery.currentData);
  const destination = resolveUploadDestination(location.pathname, rootFolder, currentFolder, authenticatedUser.id);

  const setDialogVisible = React.useCallback((visible: boolean) => {
    dialogVisibleRef.current = visible;
    setIsUploadDialogVisible(visible);
  }, []);

  const clearUploadDialog = React.useCallback(() => {
    activeDialogGenerationRef.current = null;
    dialogBusyRef.current = false;
    dialogVisibleRef.current = false;
    setIsUploadDialogVisible(false);
    setActiveDialog(null);
    dispatch(filesApi.util.invalidateTags([FILES_TAGS.FILES_SEARCH_TAG, FILES_TAGS.FILES_TAG]));
  }, [dispatch]);

  const openDialogWithState = React.useCallback(
    (state: UploadDialogState) => {
      if (dialogBusyRef.current && activeDialogGenerationRef.current !== null) {
        setDialogVisible(true);
        return;
      }
      const generation = dialogGenerationRef.current + 1;
      dialogGenerationRef.current = generation;
      activeDialogGenerationRef.current = generation;
      setActiveDialog({ generation, state });
      setDialogVisible(true);
    },
    [setDialogVisible],
  );

  const openUploadDialog = React.useCallback(
    (nextDestination: UploadDestination) => openDialogWithState({ destination: nextDestination }),
    [openDialogWithState],
  );

  const openDroppedUpload = React.useCallback(
    (files: readonly CollectedUploadFile[], nextDestination: UploadDestination, collectionMessage?: string) =>
      openDialogWithState({
        destination: nextDestination,
        initialFiles: [...files],
        ...(collectionMessage ? { collectionMessage } : {}),
      }),
    [openDialogWithState],
  );

  const trackDirectoryUploadOperation = React.useCallback((operationId: string) => {
    setTrackedOperationIds(current => (current.includes(operationId) ? current : [...current, operationId]));
  }, []);

  const removeTrackedOperation = React.useCallback((operationId: string) => {
    setTrackedOperationIds(current => current.filter(currentOperationId => currentOperationId !== operationId));
  }, []);

  const closeUploadDialog = React.useCallback(
    (generation = activeDialogGenerationRef.current) => {
      if (generation === null || generation !== activeDialogGenerationRef.current) return;
      if (dialogBusyRef.current) {
        setDialogVisible(false);
        return;
      }
      clearUploadDialog();
    },
    [clearUploadDialog, setDialogVisible],
  );

  const handleDialogBusyChange = React.useCallback(
    (generation: number, busy: boolean) => {
      if (generation !== activeDialogGenerationRef.current) return;
      dialogBusyRef.current = busy;
      if (!busy && !dialogVisibleRef.current) clearUploadDialog();
    },
    [clearUploadDialog],
  );

  const activeDialogGeneration = activeDialog?.generation ?? null;
  const handleActiveDialogBusyChange = React.useCallback(
    (busy: boolean) => {
      if (activeDialogGeneration !== null) handleDialogBusyChange(activeDialogGeneration, busy);
    },
    [activeDialogGeneration, handleDialogBusyChange],
  );
  const handleActiveDialogClose = React.useCallback(() => {
    if (activeDialogGeneration !== null) closeUploadDialog(activeDialogGeneration);
  }, [activeDialogGeneration, closeUploadDialog]);

  const handleDialogOpenChange = React.useCallback(
    (open: boolean) => {
      if (!open) closeUploadDialog(activeDialogGenerationRef.current);
    },
    [closeUploadDialog],
  );

  const contextValue = React.useMemo(
    () => ({ dialogState, openUploadDialog, openDroppedUpload, trackDirectoryUploadOperation }),
    [dialogState, openDroppedUpload, openUploadDialog, trackDirectoryUploadOperation],
  );

  React.useEffect(() => {
    const boundary = boundaryRef.current;
    if (!boundary) return;

    const handleDragEnter = (event: DragEvent) => {
      if (!isExternalFileDrag(event)) return;
      event.preventDefault();
      dragDepthRef.current += 1;
      setIsFileDragActive(true);
    };

    const handleDragOver = (event: DragEvent) => {
      if (!isExternalFileDrag(event)) return;
      event.preventDefault();
    };

    const handleDragLeave = (event: DragEvent) => {
      if (!isExternalFileDrag(event)) return;
      event.preventDefault();
      dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
      if (dragDepthRef.current === 0) setIsFileDragActive(false);
    };

    const handleDrop = async (event: DragEvent) => {
      if (!isExternalFileDrag(event)) return;
      event.preventDefault();
      const droppedDestination = destination;
      try {
        if (!droppedDestination?.canUpload || !event.dataTransfer) return;
        const files = await collectDroppedFiles(event.dataTransfer);
        if (files.length > 0) openDroppedUpload(files, droppedDestination);
      } catch (error) {
        if (!droppedDestination) {
          toast.error(GENERIC_DROP_ERROR);
          return;
        }
        if (error instanceof DirectoryTraversalUnsupportedError) {
          if (error.collectedFiles.length > 0) {
            openDroppedUpload(error.collectedFiles, droppedDestination, error.message);
          } else {
            toast.error(error.message);
          }
        } else if (error instanceof InvalidDroppedPathError) {
          toast.error(error.message);
        } else {
          toast.error(GENERIC_DROP_ERROR);
        }
      } finally {
        dragDepthRef.current = 0;
        setIsFileDragActive(false);
      }
    };

    boundary.addEventListener("dragenter", handleDragEnter);
    boundary.addEventListener("dragover", handleDragOver);
    boundary.addEventListener("dragleave", handleDragLeave);
    boundary.addEventListener("drop", handleDrop);
    return () => {
      boundary.removeEventListener("dragenter", handleDragEnter);
      boundary.removeEventListener("dragover", handleDragOver);
      boundary.removeEventListener("dragleave", handleDragLeave);
      boundary.removeEventListener("drop", handleDrop);
    };
  }, [destination, openDroppedUpload]);

  return (
    <GlobalUploadContext.Provider value={contextValue}>
      <div ref={boundaryRef} className="contents">
        {children}
        {trackedOperationIds.map(operationId => (
          <DirectoryUploadOperationTracker
            key={operationId}
            operationId={operationId}
            onTerminal={removeTrackedOperation}
          />
        ))}
        {isFileDragActive && <GlobalDropOverlay destination={destination} />}
        <Dialog open={isUploadDialogVisible && activeDialog !== null} onOpenChange={handleDialogOpenChange}>
          {activeDialog && (
            <UploadModalContent
              key={activeDialog.generation}
              destination={activeDialog.state.destination}
              initialFiles={activeDialog.state.initialFiles}
              collectionMessage={activeDialog.state.collectionMessage}
              isVisible={isUploadDialogVisible}
              onOperationStarted={trackDirectoryUploadOperation}
              onBusyChange={handleActiveDialogBusyChange}
              onClose={handleActiveDialogClose}
            />
          )}
        </Dialog>
      </div>
    </GlobalUploadContext.Provider>
  );
};
